import { useEffect, useId, useState } from 'react'
import toast from 'react-hot-toast'
import { BellAlertIcon, PlusIcon, TrashIcon, PauseIcon, PlayIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { marketAPI, priceAlertsAPI } from '../services/api'
import type { PriceAlert, AlertType, AlertStatus, TickerQuote } from '../types'
import LoadingScreen from '../components/LoadingScreen'
import Tooltip from '../components/Tooltip'
import { ALERTS_GLOSSARY } from '../config/glossary'
import { displaySymbol } from '../utils/displaySymbol'

// ---- Human-readable labels ----
const ALERT_TYPE_LABELS: Record<AlertType, string> = {
  price_above: 'Price Above',
  price_below: 'Price Below',
  price_change_up: '% Rise',
  price_change_down: '% Drop',
}

const STATUS_CONFIG: Record<AlertStatus, { label: string; classes: string }> = {
  active: { label: 'Active', classes: 'bg-success-50 text-success-700' },
  triggered: { label: 'Triggered', classes: 'bg-primary-50 text-primary-700' },
  expired: { label: 'Expired', classes: 'bg-gray-100 text-gray-500' },
  paused: { label: 'Paused', classes: 'bg-gray-100 text-gray-500' },
}

const isPriceType = (type: AlertType) => type === 'price_above' || type === 'price_below'

const usd = (value: number) =>
  value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: Math.abs(value) < 1 ? 6 : 2,
  })

// Where the threshold sits against the live price. Price alerts fire on the
// level, not a crossing, so a threshold already on the far side fires on the
// evaluator's next run -- worth saying before the user saves it.
const thresholdHint = (type: AlertType, threshold: number, price: number) => {
  const gap = ((threshold - price) / price) * 100
  if (type === 'price_above' && threshold <= price) {
    return 'The price is already above this, so the alert fires on the next check.'
  }
  if (type === 'price_below' && threshold >= price) {
    return 'The price is already below this, so the alert fires on the next check.'
  }
  return `Threshold is ${Math.abs(gap).toFixed(1)}% ${gap > 0 ? 'above' : 'below'} the current price.`
}

// % Rise and % Drop measure the move from the price at the alert's first
// check (about 5 minutes after it is saved), not from the price shown here.
const percentHint = (type: AlertType, pct: number, price: number) => {
  const level = type === 'price_change_up' ? price * (1 + pct / 100) : price * (1 - pct / 100)
  const verb = type === 'price_change_up' ? 'rises' : 'falls'
  return `Fires when the price ${verb} ${pct}% from where it is at the first check, about 5 minutes after you save it: near ${usd(level)} at today's price. Repeating alerts start again from the price they fired at.`
}

// The bell always shows a fired alert; email and push are per-alert choices.
const channelsFor = (alert: PriceAlert) =>
  ['Bell', alert.notify_email && 'Email', alert.notify_push && 'Push'].filter(Boolean).join(' · ')

// ---- New Alert Modal ----
interface NewAlertModalProps {
  isOpen: boolean
  onClose: () => void
  onCreated: (alert: PriceAlert) => void
}

const NewAlertModal = ({ isOpen, onClose, onCreated }: NewAlertModalProps) => {
  const titleId = useId()
  const [symbol, setSymbol] = useState('')
  const [alertType, setAlertType] = useState<AlertType>('price_above')
  const [threshold, setThreshold] = useState('')
  const [frequency, setFrequency] = useState<'once' | 'repeating'>('once')
  const [title, setTitle] = useState('')
  // Off unless chosen. The API turns both on when the fields are missing, so
  // they are always sent: an alert must not start emailing anyone by default.
  const [notifyEmail, setNotifyEmail] = useState(false)
  const [notifyPush, setNotifyPush] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  // The supported pairs and their live prices, from the ticker tape: the same
  // universe the API accepts and the same venue the evaluator prices from.
  // null while loading; free text is the fallback if the tape is unavailable,
  // and the API still refuses an unsupported pair.
  const [tickers, setTickers] = useState<TickerQuote[] | null>(null)
  const [tickerStale, setTickerStale] = useState(false)
  const [tickerFailed, setTickerFailed] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    setTickerFailed(false)
    marketAPI
      .getTicker()
      .then((tape) => {
        if (cancelled) return
        setTickers(tape.tickers || [])
        setTickerStale(Boolean(tape.stale))
      })
      .catch(() => {
        if (!cancelled) setTickerFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) onClose()
    }
    if (isOpen) document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [isOpen, isSubmitting, onClose])

  const reset = () => {
    setSymbol(''); setAlertType('price_above'); setThreshold(''); setFrequency('once'); setTitle('')
    setNotifyEmail(false); setNotifyPush(false)
  }

  const handleSubmit = async () => {
    if (!symbol.trim()) { toast.error('Symbol is required'); return }
    const thresh = parseFloat(threshold)
    if (isNaN(thresh) || thresh <= 0) { toast.error('Enter a valid threshold'); return }
    if (alertType === 'price_change_down' && thresh >= 100) { toast.error('A % Drop must be below 100%'); return }

    setIsSubmitting(true)
    try {
      const alert = await priceAlertsAPI.create({
        symbol: symbol.trim().toUpperCase(),
        alert_type: alertType,
        threshold: thresh,
        frequency,
        title: title.trim() || undefined,
        notify_email: notifyEmail,
        notify_push: notifyPush,
      })
      toast.success('Alert created!')
      onCreated(alert)
      reset()
      onClose()
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to create alert')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  const thresholdPrefix = isPriceType(alertType) ? '$' : ''
  const thresholdSuffix = !isPriceType(alertType) ? '%' : ''
  const thresholdPlaceholder = isPriceType(alertType) ? 'e.g. 45000' : 'e.g. 5'

  const useFreeText = tickerFailed || (tickers !== null && tickers.length === 0)
  const quote = tickers?.find((t) => t.symbol === symbol)
  const thresholdValue = parseFloat(threshold)

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 id={titleId} className="text-xl font-bold text-gray-900">New Price Alert</h2>
          <button onClick={onClose} disabled={isSubmitting} className="text-gray-500 hover:text-gray-700">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label htmlFor={`${titleId}-symbol`} className="block text-sm font-medium text-gray-700 mb-1">
              Symbol *
            </label>
            {useFreeText ? (
              <input
                id={`${titleId}-symbol`}
                type="text"
                placeholder="e.g. BTC/USDT"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            ) : (
              <select
                id={`${titleId}-symbol`}
                value={symbol}
                onChange={(e) => setSymbol(e.target.value)}
                disabled={tickers === null}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">{tickers === null ? 'Loading coins…' : 'Select a coin'}</option>
                {(tickers || []).map((t) => (
                  <option key={t.symbol} value={t.symbol}>
                    {displaySymbol(t.symbol)}
                  </option>
                ))}
              </select>
            )}
            {quote && (
              <div className="mt-2 text-sm text-gray-600" data-testid="current-price">
                <p>
                  Current price: <span className="font-semibold text-gray-900">{usd(quote.price)}</span>
                  {quote.change_24h_pct !== null && (
                    <span className={quote.change_24h_pct >= 0 ? 'text-success-600' : 'text-danger-600'}>
                      {' '}
                      ({quote.change_24h_pct >= 0 ? '+' : ''}
                      {quote.change_24h_pct.toFixed(2)}% 24h)
                    </span>
                  )}
                  {tickerStale && <span className="text-gray-500"> · delayed</span>}
                </p>
                {isPriceType(alertType) && thresholdValue > 0 && (
                  <p className="mt-1">{thresholdHint(alertType, thresholdValue, quote.price)}</p>
                )}
                {!isPriceType(alertType) && thresholdValue > 0 && (
                  <p className="mt-1" data-testid="percent-hint">{percentHint(alertType, thresholdValue, quote.price)}</p>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Alert Type</label>
            <select
              value={alertType}
              onChange={(e) => setAlertType(e.target.value as AlertType)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {(Object.entries(ALERT_TYPE_LABELS) as [AlertType, string][]).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Threshold {thresholdPrefix && `(${thresholdPrefix})`}{thresholdSuffix && `(${thresholdSuffix})`}
            </label>
            <div className="relative">
              {thresholdPrefix && (
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">{thresholdPrefix}</span>
              )}
              <input
                type="number"
                min="0"
                step="any"
                placeholder={thresholdPlaceholder}
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${thresholdPrefix ? 'pl-6' : ''}`}
              />
              {thresholdSuffix && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">{thresholdSuffix}</span>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
            <div className="flex gap-2">
              {(['once', 'repeating'] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFrequency(f)}
                  className={`flex-1 py-2 rounded-md text-sm font-medium border transition-colors capitalize ${
                    frequency === f
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              type="text"
              placeholder="e.g. BTC all-time high alert"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <fieldset>
            <legend className="block text-sm font-medium text-gray-700 mb-1">Notify me</legend>
            <p className="text-xs text-gray-500 mb-2">
              Every fired alert appears in the notification bell. Add other channels if you want them.
            </p>
            <div className="space-y-2">
              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={notifyEmail}
                  onChange={(e) => setNotifyEmail(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span>Email</span>
              </label>
              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={notifyPush}
                  onChange={(e) => setNotifyPush(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span>
                  Push
                  <span className="block text-xs text-gray-500">
                    Push delivery isn&apos;t live yet. Your choice is saved for when it is.
                  </span>
                </span>
              </label>
            </div>
          </fieldset>

          <div className="flex gap-3 pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 font-medium disabled:opacity-50"
            >
              {isSubmitting ? 'Creating...' : 'Create Alert'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ---- Alert Card ----
interface AlertCardProps {
  alert: PriceAlert
  onDelete: (id: number) => void
  onToggle: (id: number, newStatus: 'active' | 'paused') => void
}

const AlertCard = ({ alert, onDelete, onToggle }: AlertCardProps) => {
  const [isDeleting, setIsDeleting] = useState(false)
  const [isToggling, setIsToggling] = useState(false)

  const statusCfg = STATUS_CONFIG[alert.status] || { label: alert.status, classes: 'bg-gray-100 text-gray-500' }
  const isPrice = isPriceType(alert.alert_type)
  const thresholdDisplay = isPrice ? `$${alert.threshold.toLocaleString()}` : `${alert.threshold}%`
  const canPauseResume = alert.status === 'active' || alert.status === 'paused'

  const handleDelete = async () => {
    setIsDeleting(true)
    try {
      await priceAlertsAPI.delete(alert.id)
      onDelete(alert.id)
      toast.success('Alert deleted')
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to delete alert')
      setIsDeleting(false)
    }
  }

  const handleToggle = async () => {
    const newStatus = alert.status === 'active' ? 'paused' : 'active'
    setIsToggling(true)
    try {
      await priceAlertsAPI.update(alert.id, { status: newStatus })
      onToggle(alert.id, newStatus)
      toast.success(newStatus === 'paused' ? 'Alert paused' : 'Alert resumed')
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to update alert')
    } finally {
      setIsToggling(false)
    }
  }

  return (
    <div className="bg-white rounded-lg shadow p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-base font-bold text-gray-900">{displaySymbol(alert.symbol)}</span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusCfg.classes}`}>
              {statusCfg.label}
            </span>
          </div>

          {alert.title && (
            <p className="text-sm text-gray-600 mt-0.5">{alert.title}</p>
          )}

          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
            <span>
              <span className="text-gray-400">Type:</span>{' '}
              {ALERT_TYPE_LABELS[alert.alert_type]}
            </span>
            <span>
              <span className="text-gray-400">
                <Tooltip content={ALERTS_GLOSSARY.threshold}>Threshold:</Tooltip>
              </span>{' '}
              <span className="font-medium text-gray-800">{thresholdDisplay}</span>
            </span>
            {!isPrice && (
              <span data-testid="alert-start">
                <span className="text-gray-400">From:</span>{' '}
                {alert.created_price ? usd(alert.created_price) : 'set at the first check'}
              </span>
            )}
            <span>
              <span className="text-gray-400">Frequency:</span>{' '}
              <span className="capitalize">
                {alert.frequency === 'once' ? (
                  <Tooltip content={ALERTS_GLOSSARY.frequencyOnce}>{alert.frequency}</Tooltip>
                ) : (
                  alert.frequency
                )}
              </span>
            </span>
          </div>

          <div className="mt-1.5 flex flex-wrap gap-x-4 text-xs text-gray-400">
            <span>Notifies: {channelsFor(alert)}</span>
            <span>
              <Tooltip content={ALERTS_GLOSSARY.triggeredCount}>
                Triggered {alert.times_triggered} time{alert.times_triggered !== 1 ? 's' : ''}
              </Tooltip>
            </span>
            {alert.last_triggered_at && (
              <span>Last: {new Date(alert.last_triggered_at).toLocaleDateString()}</span>
            )}
            <span>Created {new Date(alert.created_at).toLocaleDateString()}</span>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {canPauseResume && (
            <button
              onClick={handleToggle}
              disabled={isToggling}
              title={alert.status === 'active' ? 'Pause alert' : 'Resume alert'}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
            >
              {alert.status === 'active'
                ? <PauseIcon className="h-4 w-4" />
                : <PlayIcon className="h-4 w-4" />
              }
            </button>
          )}
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            title="Delete alert"
            className="p-2 text-danger-500 hover:text-danger-700 hover:bg-danger-50 rounded-md transition-colors disabled:opacity-50"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}

// ---- Main Page ----
const PriceAlerts = () => {
  const [alerts, setAlerts] = useState<PriceAlert[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)

  useEffect(() => {
    priceAlertsAPI.list()
      .then((data: any) => setAlerts(Array.isArray(data) ? data : []))
      .catch(() => toast.error('Failed to load alerts'))
      .finally(() => setIsLoading(false))
  }, [])

  const handleCreated = (alert: PriceAlert) => {
    setAlerts((prev) => [alert, ...prev])
  }

  const handleDelete = (id: number) => {
    setAlerts((prev) => prev.filter((a) => a.id !== id))
  }

  const handleToggle = (id: number, newStatus: 'active' | 'paused') => {
    setAlerts((prev) =>
      prev.map((a) => (a.id === id ? { ...a, status: newStatus } : a))
    )
  }

  if (isLoading) return <LoadingScreen />

  const activeAlerts = alerts.filter((a) => a.status === 'active')
  const otherAlerts = alerts.filter((a) => a.status !== 'active')

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Price Alerts</h1>
          <p className="text-gray-500 mt-1">Get notified when assets hit your target prices</p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors font-medium"
        >
          <PlusIcon className="h-5 w-5" />
          New Alert
        </button>
      </div>

      {alerts.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <BellAlertIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg font-medium">No alerts set up yet</p>
          <p className="text-gray-400 mt-1 mb-5">Create your first alert to get notified when an asset hits your target price.</p>
          <button
            onClick={() => setIsModalOpen(true)}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 font-medium"
          >
            Create Alert
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {activeAlerts.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Active ({activeAlerts.length})
              </h2>
              <div className="space-y-3">
                {activeAlerts.map((alert) => (
                  <AlertCard
                    key={alert.id}
                    alert={alert}
                    onDelete={handleDelete}
                    onToggle={handleToggle}
                  />
                ))}
              </div>
            </div>
          )}

          {otherAlerts.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Other ({otherAlerts.length})
              </h2>
              <div className="space-y-3">
                {otherAlerts.map((alert) => (
                  <AlertCard
                    key={alert.id}
                    alert={alert}
                    onDelete={handleDelete}
                    onToggle={handleToggle}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <NewAlertModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCreated={handleCreated}
      />
    </div>
  )
}

export default PriceAlerts
